/**
 * NBD Powerlifting — Global Add-to-Cart / Cart-Drawer Bridge
 * ─────────────────────────────────────────────────────────────
 * WHY THIS EXISTS:
 * Several custom NBD pages (belt landing pages, kit page, accessory
 * template, belts collection page) add items to cart via their own
 * fetch('/cart/add.js', ...) calls, then try to "open the cart" using
 * broken methods: dispatching custom events nobody listens for
 * (cart:open, theme:cart:open...), calling window.NBDCart.open()
 * (that system was disabled site-wide), or querySelector-clicking a
 * guessed cart-icon selector that doesn't reliably match the real
 * header markup. Net effect: items get added, but the drawer never
 * opens (silent) or, in the worst case, the browser falls through to
 * a real navigation to /cart.
 *
 * The theme's ACTUAL cart drawer is Dawn's native <cart-drawer>
 * custom element (assets/cart-drawer.js), opened correctly by its
 * own .open() method, with contents refreshed via .renderContents()
 * — the same method Shopify's own product-form.js calls after a
 * successful add. This file exposes that as simple global helpers
 * any custom page can call, and also defines window.nbdATC, which
 * the belts collection page's cards call on click but which didn't
 * exist anywhere in the theme (so those buttons threw a silent JS
 * error and did nothing).
 * ─────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  /**
   * Re-fetches the current cart-drawer + cart-icon-bubble sections from
   * the server (reflecting whatever is in the cart right now) and hands
   * them to Dawn's own <cart-drawer>.renderContents(), which swaps the
   * HTML in AND opens the drawer. Safe to call after ANY successful
   * /cart/add(.js) call, regardless of how that add happened.
   * Returns a Promise<boolean> (true if a drawer was found and refreshed).
   */
  /**
   * NBD drawer first. window.NBDCart is defined by assets/nbd-cart.js, which
   * is loaded via snippets/nbd-cart-init.liquid from layout/theme.liquid.
   *
   * The reason this check exists at all: nbd-cart.js was disabled site-wide
   * because it and Dawn's <cart-drawer> BOTH opened on Add to Cart. So when the
   * NBD cart is available we use it and return immediately — Dawn's drawer is
   * never touched on this path, and two carts can no longer open. When it is
   * not available we fall through to Dawn exactly as before, so the worst case
   * is today's behaviour rather than a broken cart.
   */
  function nbdDrawer() {
    return (window.NBDCart && typeof window.NBDCart.open === 'function') ? window.NBDCart : null;
  }

  /**
   * Stage 3: refresh, never open.
   *
   * Every custom NBD page calls this after adding to cart, expecting a drawer.
   * There is no drawer any more — see the suppression block at the end of this
   * file for why. The name is kept so no caller has to change; the behaviour is
   * now "update the cart count and the View Cart button".
   */
  window.nbdRefreshCartDrawer = function () {
    if (typeof window.nbdCartStateChanged === 'function') window.nbdCartStateChanged();
    return Promise.resolve(true);
  };

  function __nbdLegacyDrawerRefresh() {
    var cart = getCartDrawer();
    if (!cart) return Promise.resolve(false);

    var sectionIds = ['cart-drawer', 'cart-icon-bubble'];
    if (typeof cart.getSectionsToRender === 'function') {
      try {
        sectionIds = cart.getSectionsToRender().map(function (s) { return s.id; });
      } catch (e) { /* keep default */ }
    }

    return fetch('/?sections=' + sectionIds.join(','))
      .then(function (r) { return r.json(); })
      .then(function (sectionsHtml) {
        if (typeof cart.renderContents === 'function') {
          cart.renderContents({ sections: sectionsHtml });
        } else if (typeof cart.open === 'function') {
          cart.open();
        }
        return true;
      })
      .catch(function (err) {
        console.error('[NBD] nbdRefreshCartDrawer failed:', err);
        // Last-resort fallback: at least open the drawer with whatever it currently has
        if (cart && typeof cart.open === 'function') { cart.open(); return true; }
        return false;
      });
  };

  /**
   * Adds a single variant to cart via the exact same request shape Dawn's
   * own product-form.js uses (multipart form + sections param), so the
   * response can be handed straight to <cart-drawer>.renderContents() —
   * this both updates AND opens the drawer in one round trip, and is the
   * preferred method going forward for any new NBD ATC code.
   */
  window.nbdCartAdd = function (variantId, quantity, onSuccess, onError) {
    quantity = quantity || 1;
    var cart = getCartDrawer();
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', quantity);
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
      body.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }
    fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: body
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          if (onError) onError(response);
          return;
        }
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }
        if (onSuccess) onSuccess(response);
      })
      .catch(function (err) {
        console.error('[NBD] nbdCartAdd failed:', err);
        if (onError) onError(err);
      });
  };

  /**
   * Called by onclick="nbdATC(this, title, price, url)" on the belts
   * collection page cards (sections/nbd-belts-collection.liquid). Each
   * card carries data-nbd-vid with the variant id to add.
   */
  window.nbdATC = function (el, title, price, url) {
    if (!el || el.classList.contains('nbd-busy')) return;
    var vid = el.getAttribute('data-nbd-vid');
    if (!vid) {
      if (url) window.location.href = url;
      return;
    }
    el.classList.add('nbd-busy');
    var orig = el.innerHTML;
    window.nbdCartAdd(
      vid,
      1,
      function () {
        el.classList.remove('nbd-busy');
        el.innerHTML = '✓ Added';
        setTimeout(function () { el.innerHTML = orig; }, 2000);
      },
      function () {
        el.classList.remove('nbd-busy');
        el.innerHTML = orig;
        // fall back to the product page so the shopper can still buy
        if (url) window.location.href = url;
      }
    );
  };



  /* ── NO SLIDE-OUT CART, SITE-WIDE ──────────────────────────────────────
     Three cart UIs existed here (Dawn's <cart-drawer>, Dawn's
     <cart-notification>, NBD's window.NBDCart) and they collided — that is why
     nbd-cart.js was disabled in the first place, and why a delegated hijack
     still produced an empty white Dawn panel: the shell was opened through a
     path the override did not cover.

     So nothing opens on add. The cart lives at /cart, which templates/cart.json
     renders through the NBD-branded sections/nbd-cart-page.liquid. The floating
     View Cart button is the only affordance, and it navigates there.

     Do NOT reintroduce a drawer without removing this block, or you will get
     two cart surfaces again. */

  function nbdSuppress(el) {
    if (!el || el.__nbdSuppressed) return;
    el.__nbdSuppressed = true;
    ['open', 'renderContents', 'show'].forEach(function (fn) {
      if (typeof el[fn] !== 'function') return;
      el[fn] = function () { nbdCartStateChanged(); };
    });
    /* Some paths toggle classes directly rather than calling a method. */
    try {
      if (window.MutationObserver) {
        new MutationObserver(function () {
          ['active', 'animate', 'open'].forEach(function (c) {
            if (el.classList.contains(c)) el.classList.remove(c);
          });
        }).observe(el, { attributes: true, attributeFilter: ['class'] });
      }
    } catch (e) { /* no-op */ }
  }

  function nbdSuppressAll() {
    ['cart-drawer', 'cart-notification'].forEach(function (sel) {
      Array.prototype.forEach.call(document.querySelectorAll(sel), nbdSuppress);
    });
  }

  /* Single place that reacts to the cart changing: refresh the header bubble
     and the floating View Cart button. Never opens anything. */
  function nbdCartStateChanged() {
    nbdRefreshBubble();
    if (typeof window.nbdViewCartRefresh === 'function') window.nbdViewCartRefresh();
    try { document.dispatchEvent(new CustomEvent('nbd:cart:updated')); } catch (e) { /* no-op */ }
  }
  window.nbdCartStateChanged = nbdCartStateChanged;

  function nbdRefreshBubble() {
    try {
      fetch('/?sections=cart-icon-bubble')
        .then(function (r) { return r.json(); })
        .then(function (s) {
          var html = s && s['cart-icon-bubble'];
          if (!html) return;
          var host = document.getElementById('cart-icon-bubble');
          if (!host) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('.shopify-section') || doc.body;
          if (fresh) host.innerHTML = fresh.innerHTML;
        })
        .catch(function () { /* cosmetic only — never block the cart */ });
    } catch (e) { /* no-op */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', nbdSuppressAll);
  } else {
    nbdSuppressAll();
  }
  window.addEventListener('load', nbdSuppressAll);

  if (window.MutationObserver && document.body) {
    var nbdMo = new MutationObserver(nbdSuppressAll);
    nbdMo.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { nbdMo.disconnect(); nbdSuppressAll(); }, 20000);
  }



})();
